/**
 * @architecture-header 1
 * @layer module
 * @domain notifications
 * @responsibility Load the current account's aggregated inbox through TanStack Query.
 * @state-owner TanStack Query
 * @allowed-imports app environment; contracts; TanStack Query
 * @forbidden routes; app bootstrap; direct Supabase client; Zustand entity copies
 * @public-api via modules/notifications/index.ts
 * @side-effects authenticated GET request outside demo mode
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useQuery } from '@tanstack/react-query';
import { type InboxItem, inboxItemSchema } from '@vispo/contracts';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';

const inboxResponseSchema = z.object({
  items: z.array(inboxItemSchema),
  unreadCount: z.number().int().nonnegative(),
});

export type InboxResponse = z.infer<typeof inboxResponseSchema>;

/**
 * `targetPath` is additive on top of the shared `.loose()` inboxItemSchema (server rows only) --
 * reading it through a cast here, rather than adding it to the shared schema, keeps the change
 * scoped to this card instead of every InboxItem consumer.
 */
export function getInboxItemTargetPath(item: InboxItem): string | undefined {
  const targetPath = (item as { targetPath?: unknown }).targetPath;
  return typeof targetPath === 'string' ? targetPath : undefined;
}

const now = new Date().toISOString();
const demoInbox: InboxItem[] = [
  {
    id: 'sub-demo-1',
    type: 'submission',
    title: '王柏睿 — 一元一次方程式',
    subtitle: '已從週三數學 A 班繳交，等待批改',
    timestamp: now,
    isRead: false,
    isCompleted: false,
    isUrgent: false,
    meta: { classId: 'class-8a', className: '週三數學 A 班', studentId: 'student-wang' },
  },
  {
    id: 'overdue-demo-1',
    type: 'overdue',
    title: '比例式練習',
    subtitle: '國三總複習已超過期限',
    timestamp: new Date(Date.now() - 86_400_000).toISOString(),
    isRead: false,
    isCompleted: false,
    isUrgent: true,
    meta: { classId: 'class-7b', className: '國三總複習' },
  },
  {
    id: 'enroll-demo-1',
    type: 'enrollment',
    title: '陳思妤',
    subtitle: '已加入週三數學 A 班',
    timestamp: new Date(Date.now() - 172_800_000).toISOString(),
    isRead: true,
    isCompleted: false,
    isUrgent: false,
    meta: { classId: 'class-8a', className: '週三數學 A 班' },
  },
];

export type InboxAudience = 'teacher' | 'student';

export const inboxKeys = {
  list: (activeContextId: string, audience?: InboxAudience) =>
    ['inbox', activeContextId, audience ?? 'all'] as const,
};

/**
 * `audience` is the caller's own portal (`session.portalAudience`), never a role read from the
 * server -- it is a display filter (badge/drawer must agree per portal), not a security boundary;
 * `recipient_user_id` from the verified token remains the sole authorization predicate server-side.
 */
export function useInbox(
  activeContextId: string,
  audience: InboxAudience | undefined,
  enabled = true,
) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: inboxKeys.list(activeContextId, audience),
    enabled,
    queryFn: async (): Promise<InboxResponse> => {
      if (environment.demoMode) {
        // Local E2E specs inject a fixture through the development-only test adapter (never a
        // bare `globalThis` read here, which would stay reachable in a production JS chunk even
        // though the adapter itself is always undefined there) to cover server-row behavior the
        // fixed demo dataset below has no server rows to exercise.
        const fixture = environment.testAdapter?.read('inbox');
        const parsedFixture = fixture ? inboxResponseSchema.safeParse(fixture) : undefined;
        if (parsedFixture?.success) return parsedFixture.data;
        return { items: demoInbox, unreadCount: demoInbox.filter((item) => !item.isRead).length };
      }
      const query = audience ? `?audience=${encodeURIComponent(audience)}` : '';
      return inboxResponseSchema.parse(
        await environment.apiClient.request<unknown>(`/api/v1/me/notifications${query}`),
      );
    },
  });
}
